import React from "react"
import { NoteIcon } from "./note-icon"
import { NoteContentLeading } from "./note-content-leading"
import { NoteDismiss } from "./note-dismiss"
import { cn } from "../utils"

function flattenChildren(
  children: React.ReactNode,
  keyPrefix = ""
): React.ReactNode[] {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
      return [child]
    }

    const key = `${keyPrefix}${child.key}`

    if (child.type === React.Fragment) {
      return flattenChildren(child.props.children, key)
    }

    return [React.cloneElement(child, { key })]
  })
}

function isLeading(child: React.ReactNode) {
  return (
    React.isValidElement(child) &&
    (child.type === NoteIcon || child.type === NoteContentLeading)
  )
}

function isDismiss(child: React.ReactNode) {
  return React.isValidElement(child) && child.type === NoteDismiss
}

/**
 * Wraps everything but the leading slot and the dismiss button in a body, so
 * the actions wrap in line with the content instead of under the leading slot,
 * however wide it is. Only direct parts and parts inside fragments are found.
 */
export function NoteInlineChildren({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) {
  const flatChildren = flattenChildren(children)

  return (
    <>
      {flatChildren.filter(isLeading)}
      <div className={cn(className)} data-slot="note-body">
        {flatChildren.filter(child => !isLeading(child) && !isDismiss(child))}
      </div>
      {flatChildren.filter(isDismiss)}
    </>
  )
}
